'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { clamp, normalize } from '@/utils';

import Space from '@/components/Space';

import Scale from './Scale';
import LilTriangle from './LilTriangle';
import {
  VIEWPORT_HEIGHT,
  BOX_SIZE,
  TRAVEL_DISTANCE,
  CONTENT_HEIGHT,
  BOX_START_OFFSET,
} from './ScrollViewProgress.constants';

type AnimationRange =
  | 'cover'
  | 'contain'
  | 'entry'
  | 'exit'
  | 'mixed';
type BoxStatus =
  | 'intersecting-bottom'
  | 'contained'
  | 'intersecting-top';

interface Props {
  animationRange?: AnimationRange;
}

interface ScrollProgressState {
  animationProgress: number | null;
  lineProgress: number;
  isProgressVisible: boolean;
}

function deriveScrollProgressState(
  scrollTop: number,
  animationRange: AnimationRange
): ScrollProgressState {
  let startOffset: number;
  let endOffset: number;
  if (animationRange === 'cover') {
    startOffset = 0;
    endOffset = TRAVEL_DISTANCE;
  } else if (animationRange === 'contain') {
    startOffset = BOX_SIZE;
    endOffset = VIEWPORT_HEIGHT;
  } else if (animationRange === 'entry') {
    startOffset = 0;
    endOffset = BOX_SIZE;
  } else if (animationRange === 'exit') {
    startOffset = VIEWPORT_HEIGHT;
    endOffset = VIEWPORT_HEIGHT + BOX_SIZE;
  } else {
    startOffset = 0;
    endOffset = TRAVEL_DISTANCE / 2;
  }

  const isProgressVisible =
    scrollTop > startOffset && scrollTop < endOffset;
  const lineProgress = clamp(
    normalize(scrollTop, startOffset, endOffset, 0, 1),
    0,
    1
  );

  const animationProgress = getProgress(scrollTop, animationRange);
  return {
    animationProgress,
    lineProgress,
    isProgressVisible,
  };
}

function getProgress(
  scrollTop: number,
  animationRange: AnimationRange
): number | null {
  if (animationRange === 'contain') {
    if (scrollTop < BOX_SIZE || scrollTop > VIEWPORT_HEIGHT) {
      return null;
    }
    return normalize(scrollTop, BOX_SIZE, VIEWPORT_HEIGHT, 0, 1);
  }

  if (animationRange === 'entry') {
    if (scrollTop > BOX_SIZE) {
      return null;
    }
    return normalize(scrollTop, 0, BOX_SIZE, 0, 1);
  }

  if (animationRange === 'exit') {
    if (
      scrollTop < VIEWPORT_HEIGHT ||
      scrollTop > VIEWPORT_HEIGHT + BOX_SIZE
    ) {
      return null;
    }
    return normalize(
      scrollTop,
      VIEWPORT_HEIGHT,
      VIEWPORT_HEIGHT + BOX_SIZE,
      0,
      1
    );
  }

  if (animationRange === 'mixed') {
    const end = TRAVEL_DISTANCE / 2;
    if (scrollTop > end) {
      return null;
    }
    return normalize(scrollTop, 0, end, 0, 1);
  }

  return clamp(scrollTop / TRAVEL_DISTANCE, 0, 1);
}

function getStatus(scrollTop: number): BoxStatus {
  if (scrollTop < BOX_SIZE) {
    return 'intersecting-bottom';
  }
  if (scrollTop > VIEWPORT_HEIGHT) {
    return 'intersecting-top';
  }
  return 'contained';
}

function getLabelIsFlipped(
  progress: number | null,
  animationRange: AnimationRange
): boolean {
  if (progress === null) {
    return false;
  }
  // The `exit` range sits at the top of the container, so we may as well always keep it underneath, to avoid the awkwardness of the flip.
  if (animationRange === 'exit') {
    return true;
  }
  const startOffset = VIEWPORT_HEIGHT;
  const endOffset =
    animationRange === 'entry'
      ? VIEWPORT_HEIGHT - BOX_SIZE
      : animationRange === 'mixed'
        ? VIEWPORT_HEIGHT / 2
        : -3;
  const lineOffset = normalize(
    progress,
    0,
    1,
    startOffset,
    endOffset
  );
  return lineOffset < 50;
}

function getProgressLineOffsetPx(
  lineProgress: number,
  animationRange: AnimationRange
): number {
  return normalize(
    lineProgress,
    0,
    1,
    animationRange === 'exit' ? BOX_SIZE - 2 : VIEWPORT_HEIGHT,
    animationRange === 'entry'
      ? VIEWPORT_HEIGHT - BOX_SIZE - 2
      : animationRange === 'mixed'
        ? VIEWPORT_HEIGHT / 2
        : -3
  );
}

function ScrollViewProgressPercentageDemo({
  animationRange = 'cover',
}: Props) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] =
    React.useState<ScrollProgressState>(() =>
      deriveScrollProgressState(0, animationRange)
    );
  const [status, setStatus] = React.useState<BoxStatus>(
    'intersecting-bottom'
  );

  const { animationProgress, lineProgress, isProgressVisible } =
    scrollProgress;
  const isLabelFlipped = getLabelIsFlipped(
    lineProgress,
    animationRange
  );
  const [
    isAnimationTimelineSupported,
    setIsAnimationTimelineSupported,
  ] = React.useState(true);

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop } = event.currentTarget;
      setScrollProgress(
        deriveScrollProgressState(scrollTop, animationRange)
      );
      setStatus(getStatus(scrollTop));
    },
    [animationRange]
  );

  React.useEffect(() => {
    const node = scrollerRef.current;

    if (!node) {
      return;
    }

    setScrollProgress(
      deriveScrollProgressState(node.scrollTop, animationRange)
    );
    setStatus(getStatus(node.scrollTop));
  }, [animationRange]);

  React.useEffect(() => {
    setIsAnimationTimelineSupported(
      CSS.supports('animation-timeline', 'scroll()')
    );
  }, []);

  let boxOpacity: number;
  if (
    animationRange === 'contain' &&
    status === 'intersecting-bottom'
  ) {
    boxOpacity = 0;
  } else if (animationRange === 'exit') {
    boxOpacity =
      animationProgress === null
        ? 1
        : normalize(animationProgress, 0, 1, 1, 0);
  } else {
    boxOpacity = animationProgress === null ? 1 : animationProgress;
  }

  const progressLineOffsetPx = getProgressLineOffsetPx(
    lineProgress,
    animationRange
  );
  const progressLineStyle: React.CSSProperties | undefined =
    isAnimationTimelineSupported && isProgressVisible
      ? undefined
      : ({
          '--line-offset': `${progressLineOffsetPx}px`,
          ...(isAnimationTimelineSupported && !isProgressVisible
            ? { animation: 'none' }
            : {}),
        } as React.CSSProperties);

  return (
    <OuterWrapper
      data-animation-range={animationRange}
      style={
        {
          '--animation-range-name':
            animationRange === 'mixed'
              ? 'cover 0% cover 50%'
              : animationRange,
          '--tick-timeline-range-name':
            animationRange === 'mixed' ? 'cover' : animationRange,
        } as React.CSSProperties
      }
    >
      <Wrapper>
        <Scale
          animationRange={animationRange}
          progress={isProgressVisible ? animationProgress : null}
          isAnimationTimelineSupported={isAnimationTimelineSupported}
          isProgressVisible={isProgressVisible}
        />
        <Container>
          <PseudoBorder />
          <Scroller ref={scrollerRef} onScroll={handleScroll}>
            <ScrollContent>
              <ProgressBox style={{ top: BOX_START_OFFSET }}>
                <Outline />
                <Fill style={{ opacity: boxOpacity }} />
              </ProgressBox>
            </ScrollContent>
          </Scroller>

          <ProgressLine
            aria-hidden
            data-is-active={isProgressVisible}
            style={progressLineStyle}
          >
            <ProgressLabel data-is-flipped={isLabelFlipped}>
              {isProgressVisible &&
                `${Math.round(lineProgress * 100)}%`}
            </ProgressLabel>
            <StyledTriangle />
          </ProgressLine>
        </Container>
      </Wrapper>
    </OuterWrapper>
  );
}

const OuterWrapper = styled(Space)`
  --yellow: hsl(45deg 100% 50%);
  width: 500px;
  max-width: 100%;
  padding: 2rem;
  margin-inline: auto;
  margin-bottom: 3rem;
  color: var(--color-text);
  timeline-scope: --content;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  gap: 4px;
  font-family: var(--font-family-mono);
`;

const Container = styled.div`
  position: relative;
  flex: 1;
  font-family: var(--font-family-mono);
  overflow: hidden;
  border-top: 2px solid var(--color-gray-400);
  border-bottom: 2px solid var(--color-gray-400);
  border-radius: 2px;
`;

const PseudoBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-left: 2px solid var(--color-gray-400);
  border-right: 2px solid var(--color-gray-400);
  pointer-events: none;
`;

const Scroller = styled.div`
  width: 100%;
  height: ${VIEWPORT_HEIGHT}px;
  overflow-y: auto;
`;

const ScrollContent = styled.div`
  position: relative;
  height: ${CONTENT_HEIGHT}px;
`;

const ProgressLine = styled.div`
  position: absolute;
  top: var(--line-offset, ${VIEWPORT_HEIGHT}px);
  left: 0;
  width: calc(50% - ${BOX_SIZE / 2}px - 12px);
  height: 3px;
  background: var(--yellow);
  pointer-events: none;
  border-radius: 0 100px 100px 0;
  opacity: 0;
  transition: opacity 150ms;

  &[data-is-active='true'] {
    opacity: 1;
  }

  @supports (animation-timeline: scroll()) {
    animation: progressLineOffset linear both;
    animation-timeline: --content;
    animation-range: var(--animation-range-name);

    ${OuterWrapper}[data-animation-range='entry'] & {
      animation-name: progressLineOffsetEntry;
    }

    ${OuterWrapper}[data-animation-range='exit'] & {
      animation-name: progressLineOffsetExit;
    }

    ${OuterWrapper}[data-animation-range='mixed'] & {
      animation-name: progressLineOffsetMixed;
    }
  }

  @property --line-offset {
    syntax: '<length>';
    inherits: false;
    initial-value: ${VIEWPORT_HEIGHT}px;
  }

  @keyframes progressLineOffset {
    from {
      --line-offset: ${VIEWPORT_HEIGHT}px;
    }
    to {
      --line-offset: -3px;
    }
  }

  @keyframes progressLineOffsetEntry {
    from {
      --line-offset: ${VIEWPORT_HEIGHT}px;
    }
    to {
      --line-offset: ${VIEWPORT_HEIGHT - BOX_SIZE}px;
    }
  }

  @keyframes progressLineOffsetExit {
    from {
      --line-offset: ${BOX_SIZE - 3}px;
    }
    to {
      --line-offset: -3px;
    }
  }

  @keyframes progressLineOffsetMixed {
    from {
      --line-offset: ${VIEWPORT_HEIGHT}px;
    }
    to {
      --line-offset: ${VIEWPORT_HEIGHT / 2}px;
    }
  }
`;

const StyledTriangle = styled(LilTriangle)`
  transform: translateY(calc(-50% + 1.5px));
`;

const ProgressLabel = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 6px);
  font-size: 0.8rem;
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: white;
  white-space: nowrap;

  &[data-is-flipped='true'] {
    bottom: auto;
    top: calc(100% + 6px);
  }
`;

const ProgressBox = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin-inline: auto;
  width: ${BOX_SIZE}px;
  height: ${BOX_SIZE}px;
  border-radius: 4px;
  view-timeline: --content;
`;

const Outline = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 4px dashed var(--color-gray-200);
  border-radius: inherit;
`;

const Fill = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: var(--yellow);
`;

export default ScrollViewProgressPercentageDemo;
